import React, { useState, useRef, useEffect } from 'react';
import { Menu, Bell, Sun, Moon, User, LogOut, Settings, ChevronDown } from 'lucide-react';
import './ClubTopbar.css';

/**
 * Topbar estilo Tailwind para el ClubDashboard.
 *
 * Props:
 *  - pageTitle: string, título de la página activa
 *  - onToggleSidebar: () => void, alterna sidebar (colapsar en desktop, abrir en móvil)
 *  - clubName: string
 *  - clubLogoUrl: string | null
 *  - initials: string
 *  - darkMode: boolean
 *  - onToggleDarkMode: () => void
 *  - onGoProfile: () => void
 *  - onGoSettings: () => void
 *  - onLogout: () => void
 *  - notificationsCount: number
 */
const ClubTopbar = ({
  pageTitle = 'Panel',
  onToggleSidebar,
  clubName = '',
  clubLogoUrl = null,
  initials = 'CL',
  role = 'Club',
  roleLabel = 'Cuenta de club',
  showSettings = true,
  darkMode = false,
  onToggleDarkMode,
  onGoProfile,
  onGoSettings,
  onLogout,
  notificationsCount = 0,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Cerrar dropdown al click afuera
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <header className="ct-topbar">
      <div className="ct-topbar-left">
        <button
          className="ct-icon-btn"
          onClick={onToggleSidebar}
          aria-label="Alternar menú"
        >
          <Menu size={20} />
        </button>
        <h1 className="ct-page-title">{pageTitle}</h1>
      </div>

      <div className="ct-topbar-right">
        {/* Dark mode temporalmente deshabilitado */}
        {false && (
          <button
            className="ct-icon-btn ct-icon-btn-theme"
            onClick={onToggleDarkMode}
            aria-label={darkMode ? 'Modo claro' : 'Modo oscuro'}
            title={darkMode ? 'Modo claro' : 'Modo oscuro'}
          >
            {darkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        )}

        <button
          className="ct-icon-btn ct-icon-btn-bell"
          aria-label="Notificaciones"
          title="Notificaciones"
        >
          <Bell size={18} />
          {notificationsCount > 0 && (
            <span className="ct-bell-badge">{notificationsCount > 9 ? '9+' : notificationsCount}</span>
          )}
        </button>

        <div className="ct-profile-wrap" ref={menuRef}>
          <button
            className="ct-profile-btn"
            onClick={() => setMenuOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <span className="ct-avatar">
              {clubLogoUrl ? (
                <img src={clubLogoUrl} alt={clubName} />
              ) : (
                <span>{initials}</span>
              )}
            </span>
            <span className="ct-profile-meta">
              <span className="ct-profile-name">{clubName || role}</span>
              <span className="ct-profile-role">{role}</span>
            </span>
            <ChevronDown size={16} className={`ct-chev ${menuOpen ? 'open' : ''}`} />
          </button>

          {menuOpen && (
            <div className="ct-dropdown" role="menu">
              <div className="ct-dropdown-header">
                <div className="ct-avatar ct-avatar-lg">
                  {clubLogoUrl ? <img src={clubLogoUrl} alt={clubName} /> : <span>{initials}</span>}
                </div>
                <div className="ct-dropdown-header-info">
                  <strong>{clubName || role}</strong>
                  <small>{roleLabel}</small>
                </div>
              </div>

              <button
                className="ct-dropdown-item"
                onClick={() => { setMenuOpen(false); onGoProfile && onGoProfile(); }}
                role="menuitem"
              >
                <User size={16} />
                <span>Ver perfil</span>
              </button>

              {showSettings && (
                <button
                  className="ct-dropdown-item"
                  onClick={() => { setMenuOpen(false); onGoSettings && onGoSettings(); }}
                  role="menuitem"
                >
                  <Settings size={16} />
                  <span>Configuración</span>
                </button>
              )}

              {/* Dark mode temporalmente deshabilitado */}
              {false && (
                <button
                  className="ct-dropdown-item"
                  onClick={() => { onToggleDarkMode && onToggleDarkMode(); }}
                  role="menuitem"
                >
                  {darkMode ? <Sun size={16} /> : <Moon size={16} />}
                  <span>{darkMode ? 'Modo claro' : 'Modo oscuro'}</span>
                </button>
              )}

              <div className="ct-dropdown-divider" />

              <button
                className="ct-dropdown-item ct-dropdown-item-danger"
                onClick={() => { setMenuOpen(false); onLogout && onLogout(); }}
                role="menuitem"
              >
                <LogOut size={16} />
                <span>Cerrar sesión</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default ClubTopbar;
